import React, {ChangeEvent, useEffect, useRef, useState} from "react";
import {IconButton, Paper, TextField} from "@material-ui/core";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import CancelIcon from "@material-ui/icons/Cancel";
import SaveIcon from "@material-ui/icons/Save";
import {InvType} from "../Utils/Enums";
import {gql, useMutation, useQuery} from "@apollo/client";
import {createSecureInventory, updateSecureInventory} from "../graphql/mutations";
import {Building, Inventory} from "../API";
import {useMainApplicationContext} from "../hooks/useMainApplicationContext";
import {useErrorContext} from "../hooks/useErrorContext";
import {useTranslation} from "react-i18next";
import {BtnButtonInputStyle, GeneralInputStyles} from "../styles/InputFieldStyle";
import {checkForValidInputNaming} from "../Utils/Helpers";
import {usePermissionHelper} from "../hooks/usePermissionHelper";

interface Props {
    setIsShowCreateInv: (value: boolean) => void
    type: InvType
    selectedInv: Inventory | undefined
    setSelectedInv: (value: Inventory | undefined) => void
    setIsShowAlert: (value: boolean) => void
    inventories: [] | Inventory []
}

const CreateInventoryComponent: React.FC<Props> = (props) => {

    const {setIsShowCreateInv, type, selectedInv, setIsShowAlert, setSelectedInv, inventories} = props;
    const {selectedBuildingInventory} = useMainApplicationContext();
    const {reportError} = useErrorContext();
    const [newInventoryName, setNewInventoryName] = useState(() => {
        if (selectedInv) {
            return selectedInv.name
        }
        return ""
    })
    const [listOfManagedBuildings, setListOfManagedBuildings] = useState<Building[]>([]);
    const {currentUser, managedBuildings} = useMainApplicationContext();
    const {hasManagementForBuilding} = usePermissionHelper();

    const [createInventoryMutation] = useMutation(gql(createSecureInventory))
    const [updateInventoryMutation] = useMutation(gql(updateSecureInventory))


    useEffect(function focusTextFieldWhenRendered() {
        if (nameInputRef?.current) nameInputRef.current.focus();
    }, [])

    useEffect(function updateTextFieldValue() {
        setNewInventoryName(selectedInv ? selectedInv.name : "")
    }, [selectedInv])

    useEffect(() => {
        setListOfManagedBuildings(currentUser.isAdmin ? managedBuildings : managedBuildings.filter(building => hasManagementForBuilding(building)))
    }, [managedBuildings]);

    const nameInputRef = useRef<HTMLInputElement>(null);

    const showErrorMessage = () => {
        setIsShowAlert(true)
        setTimeout(() => {
            setIsShowAlert(false)
        }, 4000)
    }

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        setNewInventoryName(event.target.value)
    }

    const isConflictingWithInventoryNamesInOrgUnit = async (): Promise<boolean> => {
        //This query returns all inventory items with the same name as the currently typed name in the input box

        if (inventories === undefined || inventories.length === 0) return false;

        return inventories.some((item: Inventory) =>
            item.type === type &&
            item.buildingId === selectedBuildingInventory?.buildingId &&
            item.nameLowerCased.trim() === newInventoryName.toLocaleLowerCase().trim())
    }

    const handleSave = async () => {
        const isConflicting = await isConflictingWithInventoryNamesInOrgUnit()
        if (isConflicting) {
            showErrorMessage()
            return
        }

        if (selectedInv) handleUpdateInventory()
        else handleCreateInventory()


        setSelectedInv(undefined)
    }

    const handleCreateInventory = () => {
        createInventoryMutation({
            variables: {
                input: {
                    buildingId: selectedBuildingInventory?.buildingId,
                    type: type,
                    name: newInventoryName.trim(),
                }
            }
        })
            .then(() => setIsShowCreateInv(false))
            .catch((err) => reportError(err, "", "CreateInventoryComponent handleCreateInventory"))
    }

    const handleUpdateInventory = () => {
        updateInventoryMutation({
            variables: {
                input: {
                    inventoryId: selectedInv!.inventoryId,
                    name: newInventoryName.trim(),
                }
            }
        })
            .then(() => setIsShowCreateInv(false))
            .catch((err) => reportError(err, "", "CreateInventoryComponent handleUpdateInventory"))
    }

    const classes = BtnButtonInputStyle();

    const MAX_ITEM_NAME_LENGTH = 100;
    const isNewInventoryNameValid = checkForValidInputNaming(newInventoryName, MAX_ITEM_NAME_LENGTH)

    const styles = GeneralInputStyles;
    const {t} = useTranslation();

    return (
        <Paper style={styles.paperStyle} data-testid={"createInventoryComponent"}>
            <Box style={styles.textFieldBoxStyle}>
                <TextField
                    style={{marginRight: "10px", width: "100%"}}
                    label={type}
                    value={newInventoryName}
                    onChange={handleInputChange}
                    inputRef={nameInputRef}
                    inputProps={{"data-testid": "inventoryName-text"}}
                />
            </Box>

            <Box style={styles.toolTipsBoxStyle}>
                <Tooltip title={t("confirm_dialog_ok_button-text")}>
                    <span>
                        <IconButton onClick={handleSave} disabled={!isNewInventoryNameValid} data-testid={"save-btn"}>
                            <SaveIcon className={isNewInventoryNameValid ? classes.btnEnabled : classes.btnDisabled}
                                      style={{cursor: "pointer"}} fontSize="large"/>
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={t("confirm_dialog_cancel_button-text")}>
                    <IconButton onClick={() => setIsShowCreateInv(false)} data-testid={"cancel-btn"}>
                        <CancelIcon style={{cursor: "pointer"}} color="primary" fontSize="large"/>
                    </IconButton>
                </Tooltip>
            </Box>
        </Paper>
    )
}
export default CreateInventoryComponent;
